/**
 * ========================================
 * Grade Settings Page - 등급 판정 기준 설정
 * ========================================
 * 
 * 요구사항: 자기장 등급 판정 기준 관리
 * - 약함, 중간, 강함, 매우강함, 극강 기준 설정
 * - UX 표시 상한 설정 (120 µT)
 * - 관리자 수정 가능
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { 
  HiLogout,
  HiSparkles,
  HiViewGrid,
  HiMap,
  HiChartBar,
  HiCog,
  HiPlus,
  HiPencil,
  HiTrash,
  HiX,
  HiCheck,
  HiLockClosed
} from 'react-icons/hi';
import { gradeAPI, adminAuth } from '../services/api.service';
import ChangePasswordModal from '../components/ChangePasswordModal';

export default function GradeSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [activeSection, setActiveSection] = useState('rules'); // 'rules' or 'settings'
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  // 등급 규칙 조회
  const { data: rulesData, isLoading: rulesLoading } = useQuery({
    queryKey: ['gradeRules'],
    queryFn: gradeAPI.getGradeRules,
  });

  // 표시 설정 조회
  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['displaySettings'],
    queryFn: gradeAPI.getDisplaySettings,
  });

  const rules = rulesData?.data?.rules || [];
  const settings = settingsData?.data?.settings || {};

  // 등급 규칙 생성/수정 Mutation
  const ruleMutation = useMutation({
    mutationFn: gradeAPI.createOrUpdateGradeRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradeRules'] });
      setIsFormOpen(false);
      setEditingRule(null);
      reset();
      alert('등급 규칙이 저장되었습니다.');
    },
    onError: (error) => {
      alert('저장 실패: ' + (error.response?.data?.error || error.message));
    },
  });

  // 등급 규칙 삭제 Mutation
  const deleteMutation = useMutation({
    mutationFn: gradeAPI.deleteGradeRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradeRules'] });
      alert('등급 규칙이 삭제되었습니다.');
    },
    onError: (error) => {
      alert('삭제 실패: ' + (error.response?.data?.error || error.message));
    },
  });

  // 표시 설정 업데이트 Mutation
  const settingMutation = useMutation({
    mutationFn: ({ key, value }) => gradeAPI.updateDisplaySetting(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['displaySettings'] });
      alert('설정이 업데이트되었습니다.');
    },
    onError: (error) => {
      alert('업데이트 실패: ' + (error.response?.data?.error || error.message));
    },
  });

  const handleLogout = () => {
    adminAuth.logout();
    navigate('/login');
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setIsFormOpen(true);
    reset({
      grade: rule.grade,
      grade_display: rule.grade_display,
      min_ut_threshold: rule.min_ut_threshold,
      max_ut_threshold: rule.max_ut_threshold,
      description: rule.description || '',
      warning_message: rule.warning_message || '',
    });
  };

  const handleDelete = (rule) => {
    if (confirm(`"${rule.grade_display}" 등급 규칙을 삭제하시겠습니까?`)) {
      deleteMutation.mutate(rule.id);
    }
  };

  const onSubmitRule = (data) => {
    ruleMutation.mutate({
      ...data,
      min_ut_threshold: parseFloat(data.min_ut_threshold),
      max_ut_threshold: parseFloat(data.max_ut_threshold),
    });
  };

  const handleSettingUpdate = (key, value) => {
    settingMutation.mutate({ key, value });
  };

  if (rulesLoading || settingsLoading) {
    return (
      <div className="min-h-screen bg-theme-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary mx-auto mb-4"></div>
          <p className="text-theme-text-secondary">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-background">
      {/* Header */}
      <motion.header
        className="bg-gradient-to-r from-indigo-600 via-pink-600 to-indigo-600 backdrop-blur-xl shadow-2xl border-b border-white/20"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6 py-5">
          <div className="flex justify-between items-center">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  className="p-1 bg-white/20 backdrop-blur-md rounded-full shadow-lg border-2 border-white/40 relative overflow-hidden"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <img 
                    src="/Frame 34.png" 
                    alt="시스템 아이콘" 
                    className="w-10 h-10 object-cover rounded-full"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement.querySelector('.icon-fallback');
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                  <HiSparkles className="w-10 h-10 text-white icon-fallback hidden rounded-full" />
                </motion.div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white tracking-tight drop-shadow-lg">
                    팔팔구구 <span className="bg-gradient-to-r from-yellow-200 to-pink-200 bg-clip-text text-transparent">메타보맵</span>
                  </h1>
                  <p className="text-sm font-medium text-white/90 mt-0.5">등급 판정 기준 설정</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="flex items-center space-x-4"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.button
                onClick={() => setIsPasswordModalOpen(true)}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-md border border-white/30 rounded-xl hover:bg-white/30 flex items-center gap-2 shadow-lg transition-all"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <HiLockClosed className="w-4 h-4" />
                비밀번호 변경
              </motion.button>
              <motion.button
                onClick={handleLogout}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-md border border-white/30 rounded-xl hover:bg-white/30 flex items-center gap-2 shadow-lg transition-all"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <HiLogout className="w-4 h-4" />
                로그아웃
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-[95%] mx-auto px-2 sm:px-4 lg:px-6 py-8">
        {/* Navigation Tabs */}
        <motion.div
          className="mb-8 bg-theme-surface backdrop-blur-lg rounded-2xl shadow-lg border-2 border-theme-primary/20 p-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <nav className="flex space-x-2">
            <motion.button
              onClick={() => navigate('/dashboard')}
              className="py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50 transition-all"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiViewGrid className="w-5 h-5" />
              대시보드
            </motion.button>
            <motion.button
              onClick={() => navigate('/spots')}
              className="py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50 transition-all"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiMap className="w-5 h-5" />
              스팟 관리
            </motion.button>
            <motion.button
              onClick={() => navigate('/spots?tab=sensorData')}
              className="py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50 transition-all"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiChartBar className="w-5 h-5" />
              스팟별 측정자료 조회
            </motion.button>
            <motion.button
              onClick={() => navigate('/grade-settings')}
              className="py-3 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 relative bg-gradient-to-r from-theme-primary to-theme-secondary text-white shadow-lg"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <HiCog className="w-5 h-5" />
              등급 판정 기준
            </motion.button>
          </nav>
        </motion.div>

        {/* Section Tabs */}
        <motion.div
          className="mb-6 bg-theme-surface backdrop-blur-lg rounded-2xl shadow-lg border-2 border-theme-primary/20 p-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <nav className="flex space-x-2">
            <motion.button
              onClick={() => setActiveSection('rules')}
              className={`py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
                activeSection === 'rules'
                  ? 'bg-gradient-to-r from-theme-primary to-theme-secondary text-white shadow-lg'
                  : 'text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50'
              }`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              등급 규칙
            </motion.button>
            <motion.button
              onClick={() => setActiveSection('settings')}
              className={`py-3 px-6 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
                activeSection === 'settings'
                  ? 'bg-gradient-to-r from-theme-primary to-theme-secondary text-white shadow-lg'
                  : 'text-theme-text-secondary hover:text-theme-primary hover:bg-theme-surface/50'
              }`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              표시 설정
            </motion.button>
          </nav>
        </motion.div>

        {activeSection === 'rules' ? (
          <>
            {/* 등급 규칙 목록 */}
            <motion.div
              className="bg-theme-surface backdrop-blur-lg rounded-2xl shadow-xl border-2 border-theme-primary/20 overflow-hidden mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="px-6 py-5 bg-theme-surface border-b border-theme-primary/20 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-extrabold text-theme-text">등급 판정 기준</h3>
                  <p className="mt-1 text-sm font-medium text-theme-text-secondary">자기장 세기(µT) 기준 등급 분류 규칙</p>
                </div>
                <motion.button
                  onClick={() => {
                    setIsFormOpen(true);
                    setEditingRule(null);
                    reset({
                      grade: '',
      grade_display: '',
      min_ut_threshold: '',
      max_ut_threshold: '',
      description: '',
      warning_message: '',
    });
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-theme-primary to-theme-secondary text-white text-sm font-medium rounded-lg hover:from-indigo-500 hover:to-pink-500 flex items-center gap-2 shadow-lg"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <HiPlus className="w-4 h-4" />
                  새 규칙 추가
                </motion.button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-theme-primary/20">
                  <thead className="bg-theme-surface">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-theme-text-secondary uppercase tracking-wider">등급</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-theme-text-secondary uppercase tracking-wider">범위 (µT)</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-theme-text-secondary uppercase tracking-wider">설명</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-theme-text-secondary uppercase tracking-wider">작업</th>
                    </tr>
                  </thead>
                  <tbody className="bg-theme-background divide-y divide-theme-primary/20">
                    {rules.map((rule, index) => (
                      <motion.tr
                        key={rule.id}
                        className={`hover:bg-theme-surface/50 transition-all ${
                          index % 2 === 0 ? 'bg-theme-background' : 'bg-theme-surface/30'
                        }`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-theme-text">{rule.grade_display}</div>
                          <div className="text-xs text-theme-text-secondary">{rule.grade}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-theme-text">
                            {rule.min_ut_threshold} ~ {rule.max_ut_threshold === 999999 ? '∞' : rule.max_ut_threshold} µT
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-theme-text-secondary">{rule.description || '-'}</div>
                          {rule.warning_message && (
                            <div className="text-xs text-red-400 mt-1">{rule.warning_message}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <motion.button
                              onClick={() => handleEdit(rule)}
                              className="p-2 bg-theme-primary/20 text-theme-primary rounded-lg hover:bg-theme-primary/30 transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              title="수정"
                            >
                              <HiPencil className="w-4 h-4" />
                            </motion.button>
                            <motion.button
                              onClick={() => handleDelete(rule)}
                              className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              title="삭제"
                            >
                              <HiTrash className="w-4 h-4" />
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </>
        ) : (
          /* 표시 설정 */
          <motion.div
            className="bg-theme-surface backdrop-blur-lg rounded-2xl shadow-xl border-2 border-theme-primary/20 p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-xl font-extrabold text-theme-text mb-6">표시 설정</h3>
            
            <div className="space-y-6">
              {/* UX 표시 상한 */}
              <div>
                <label className="block text-sm font-semibold text-theme-text-secondary mb-2">
                  UX 표시 상한 (µT)
                </label>
                <p className="text-xs text-theme-text-secondary mb-3">
                  120 µT 초과는 "120+"로 표시되고, 시각화는 120에서 클램프됩니다.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    defaultValue={settings.ux_display_max_ut || '120'}
                    className="px-4 py-2 bg-theme-background border-2 border-theme-primary/30 rounded-xl text-sm font-medium text-theme-text focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all"
                    onBlur={(e) => {
                      const newValue = e.target.value;
                      if (newValue !== settings.ux_display_max_ut) {
                        handleSettingUpdate('ux_display_max_ut', newValue);
                      }
                    }}
                  />
                  <span className="text-sm text-theme-text-secondary">µT</span>
                </div>
              </div>

              {/* 오버레인지 임계값 */}
              <div>
                <label className="block text-sm font-semibold text-theme-text-secondary mb-2">
                  오버레인지 임계값 (%)
                </label>
                <p className="text-xs text-theme-text-secondary mb-3">
                  센서 최대 범위의 몇 % 이상이면 오버레인지로 판단합니다.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    defaultValue={settings.over_range_threshold_percent || '95'}
                    className="px-4 py-2 bg-theme-background border-2 border-theme-primary/30 rounded-xl text-sm font-medium text-theme-text focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all"
                    onBlur={(e) => {
                      const newValue = e.target.value;
                      if (newValue !== settings.over_range_threshold_percent) {
                        handleSettingUpdate('over_range_threshold_percent', newValue);
                      }
                    }}
                  />
                  <span className="text-sm text-theme-text-secondary">%</span>
                </div>
              </div>

              {/* 급격한 변동 임계값 */}
              <div>
                <label className="block text-sm font-semibold text-theme-text-secondary mb-2">
                  급격한 변동 임계값 (µT)
                </label>
                <p className="text-xs text-theme-text-secondary mb-3">
                  1~2초 내 이 값 이상 변동 시 환경 영향 가능으로 판단합니다.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    defaultValue={settings.rapid_change_threshold_ut || '20'}
                    className="px-4 py-2 bg-theme-background border-2 border-theme-primary/30 rounded-xl text-sm font-medium text-theme-text focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all"
                    onBlur={(e) => {
                      const newValue = e.target.value;
                      if (newValue !== settings.rapid_change_threshold_ut) {
                        handleSettingUpdate('rapid_change_threshold_ut', newValue);
                      }
                    }}
                  />
                  <span className="text-sm text-theme-text-secondary">µT</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* 등급 규칙 폼 모달 */}
        {isFormOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setIsFormOpen(false);
              setEditingRule(null);
              reset();
            }}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            
            <motion.div
              className="relative bg-theme-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-theme-primary/20"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-theme-primary/20 bg-theme-surface flex items-center justify-between">
                <h3 className="text-xl font-bold text-theme-text">
                  {editingRule ? '등급 규칙 수정' : '새 등급 규칙 추가'}
                </h3>
                <motion.button
                  onClick={() => {
                    setIsFormOpen(false);
                    setEditingRule(null);
                    reset();
                  }}
                  className="p-2 text-theme-text-secondary hover:text-theme-text rounded-lg hover:bg-theme-background transition-colors"
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <HiX className="w-5 h-5" />
                </motion.button>
              </div>

              <div className="overflow-y-auto flex-1 p-6">
                <form onSubmit={handleSubmit(onSubmitRule)} className="space-y-4" id="grade-rule-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                        등급 코드 *
                      </label>
                      <select
                        {...register('grade', { required: true })}
                        className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text"
                        disabled={!!editingRule}
                      >
                        <option value="">선택하세요</option>
                        <option value="weak">weak (약함)</option>
                        <option value="medium">medium (중간)</option>
                        <option value="strong">strong (강함)</option>
                        <option value="very_strong">very_strong (매우 강함)</option>
                        <option value="extreme">extreme (극강)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                        등급 표시명 *
                      </label>
                      <input
                        {...register('grade_display', { required: true })}
                        className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text"
                        placeholder="예: 약함"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                        최소 µT 임계값 *
                      </label>
                      <input
                        {...register('min_ut_threshold', { required: true })}
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                        최대 µT 임계값 *
                      </label>
                      <input
                        {...register('max_ut_threshold', { required: true })}
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                      설명
                    </label>
                    <textarea
                      {...register('description')}
                      rows="2"
                      className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-text-secondary mb-1">
                      경고 메시지 (극강일 경우)
                    </label>
                    <input
                      {...register('warning_message')}
                      className="w-full px-3 py-2 border border-theme-primary/30 rounded-lg text-sm bg-theme-background text-theme-text"
                      placeholder="예: 인공/금속 영향 가능"
                    />
                  </div>
                </form>
              </div>

              <div className="px-6 py-4 border-t border-theme-primary/20 bg-theme-surface flex space-x-3">
                <motion.button
                  type="button"
                  onClick={() => {
                    setIsFormOpen(false);
                    setEditingRule(null);
                    reset();
                  }}
                  className="flex-1 py-3 bg-theme-background text-theme-text-secondary font-medium rounded-lg hover:bg-theme-background/80 transition-colors border border-theme-primary/20"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  취소
                </motion.button>
                <motion.button
                  type="submit"
                  form="grade-rule-form"
                  className="flex-1 py-3 bg-gradient-to-r from-theme-primary to-theme-secondary text-white font-medium rounded-lg hover:from-indigo-500 hover:to-pink-500 transition-all shadow-lg"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {editingRule ? '수정하기' : '생성하기'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </main>

      {/* 비밀번호 변경 모달 */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </div>
  );
}
